/**
 * The identity portal — a full-page transition that says "you are now someone else" when the app switches tenant
 * (or org email).
 *
 *   1. OUT: a disc in the NEW identity's avatar gradient grows out of the taskbar avatar until it covers the page;
 *      the new identity's avatar flies out of the small one to the centre, its name and email rising in under it.
 *   2. HOLD: the switch runs behind the disc (it stays until the new tenant's page has painted).
 *   3. IN: the disc collapses back into the avatar and the big avatar flies home with it — the new identity lands
 *      where it now lives.
 *
 * Every switch goes through `switchTenant`, which calls `playIdentitySwitch` — so the avatar menu, the identity
 * picker and the link review dialog all get it. The avatar is found by `data-identity-avatar` (AvatarMenu);
 * without one the portal opens from the bottom-left corner. Reduced motion: a short fade, no flight.
 *
 * Animated with the Web Animations API on `clip-path` / `transform` / `opacity` only — composited, no layout,
 * no persistent `backdrop-filter` (docs/DESIGN_DOC.md rule 2: this surface is transient and never resizes).
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@papan/i18n'
import { avatarBg, getInitials } from './avatar'
import { useTheme, getThemeClass } from './ThemeProvider'

export interface IdentitySwitchCard {
  /** The avatar seed — the org's name, or the person's email for a personal account (AvatarMenu's rule). */
  seed: string
  /** Shown big in the centre. */
  title: string
  /** Under the title — the org email the person acts as, or "Personal". */
  subtitle?: string
  /** The person's role there (`owner`, `admin`, …), shown as a chip — a role id, translated here. */
  role?: string
}

type Directory = (tenantId: string, orgEmail?: string) => IdentitySwitchCard | null
let directory: Directory | null = null
/** The app tells the portal who a tenant is (it holds the tenant list and the user); null = no portal. */
export function registerIdentityDirectory(fn: Directory | null): void { directory = fn }
export function identityCardFor(tenantId: string, orgEmail?: string): IdentitySwitchCard | null {
  try { return directory?.(tenantId, orgEmail) ?? null } catch { return null }
}

type Run = {
  card: IdentitySwitchCard
  /** Called by the host once the disc covers the page — the switch starts then. */
  covered: () => void
  /** Settles when the switch has run (its own outcome is the caller's). */
  switched: Promise<unknown>
  done: () => void
}
let host: ((run: Run) => void) | null = null

/**
 * Play the portal around `doSwitch`. Resolves when the page is revealed again. If no host is mounted (or the card
 * is unknown) it just runs the switch.
 */
export async function playIdentitySwitch(card: IdentitySwitchCard | null, doSwitch: () => Promise<unknown> | unknown): Promise<void> {
  if (!card || !host) { await doSwitch(); return }
  let covered!: () => void
  const coveredP = new Promise<void>((r) => { covered = r })
  let finished!: () => void
  const finishedP = new Promise<void>((r) => { finished = r })
  // The switch starts only once the page is covered — the new tenant never flashes in under a half-open disc.
  const switched = coveredP.then(() => doSwitch())
  host({ card, covered, switched, done: finished })
  await Promise.all([finishedP, switched])
}

const OUT_MS = 520
const IN_MS = 600
/** The covered screen stays at least this long, so the name and role are READ, not glimpsed (~0.3 s fully shown, on top of the rise). */
const HOLD_MIN_MS = 400
const EASE_OUT = 'cubic-bezier(0.76, 0, 0.24, 1)'
const EASE_IN = 'cubic-bezier(0.65, 0, 0.35, 1)'
const AVATAR_PX = 40
const CARD_PX = 112

function originOf(): { x: number; y: number } {
  const el = document.querySelector<HTMLElement>('[data-identity-avatar]')
  const r = el?.getBoundingClientRect()
  if (r && r.width > 0) return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
  return { x: 28, y: window.innerHeight - 28 }
}
const nextPaint = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export function IdentitySwitchCurtainHost() {
  const { t } = useTranslation('common')
  // Outside ThemedArea: apply the theme class ourselves, with `ws-clear` so it brings the CSS variables without its
  // solid background (which would cover the page before the disc has grown).
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [run, setRun] = useState<Run | null>(null)
  const discRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<HTMLDivElement>(null)
  const avatarRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  // Geometry, fixed for the run: where the big avatar rests (the card sits 28px above centre) and the radius of the
  // disc that covers the page around it.
  const [geo, setGeo] = useState<{ cx: number; cy: number; cover: number } | null>(null)

  useEffect(() => {
    host = (r) => {
      const w = window.innerWidth, h = window.innerHeight
      const cx = w / 2, cy = h / 2 - 28
      setGeo({ cx, cy, cover: Math.hypot(Math.max(cx, w - cx), Math.max(cy, h - cy)) + 24 })
      setRun(r)
    }
    return () => { host = null }
  }, [])

  useEffect(() => {
    if (!run || !geo) return
    const disc = discRef.current, ring = ringRef.current, avatar = avatarRef.current, text = textRef.current
    if (!disc || !ring || !avatar || !text) return
    let cancelled = false
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const { cx, cy, cover } = geo
    // The disc is a REAL circle (radius `cover`, centred where the avatar rests), moved and scaled with `transform`
    // only — composited on the GPU, so it stays smooth while the new tenant's page mounts underneath (a clip-path
    // animation runs on the main thread and stuttered there). The disc and the avatar are ONE shape: same centre,
    // same duration, same easing, two keyframes each — the disc's edge never leads or trails the avatar.
    const discAt = (px: number, py: number, r: number) => `translate(${px - cx}px, ${py - cy}px) scale(${r / cover})`
    const avatarAt = (px: number, py: number, scale: number) => `translate(${px - cx}px, ${py - cy}px) scale(${scale})`
    const r0 = AVATAR_PX / 2

    ;(async () => {
      try {
        const from = originOf()
        if (reduced) {
          disc.style.transform = 'none'
          await disc.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 140, fill: 'forwards' }).finished
          avatar.style.opacity = '1'; text.style.opacity = '1'
        } else {
          const timing = { duration: OUT_MS, easing: EASE_OUT, fill: 'forwards' as const }
          avatar.style.opacity = '1'
          disc.style.opacity = '1'
          const grow = disc.animate([{ transform: discAt(from.x, from.y, r0) }, { transform: discAt(cx, cy, cover) }], timing)
          avatar.animate([{ transform: avatarAt(from.x, from.y, AVATAR_PX / CARD_PX) }, { transform: avatarAt(cx, cy, 1) }], timing)
          text.animate(
            [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }],
            { duration: 280, delay: OUT_MS - 200, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' },
          )
          // A soft shockwave as the disc lands — the "something changed" beat.
          ring.animate(
            [{ transform: 'scale(0.6)', opacity: 0 }, { transform: 'scale(0.6)', opacity: 0.55, offset: 0.15 }, { transform: 'scale(2.4)', opacity: 0 }],
            { duration: 900, delay: OUT_MS - 140, easing: 'cubic-bezier(0.1, 0.7, 0.3, 1)', fill: 'forwards' },
          )
          await grow.finished
        }
        if (cancelled) return
        run.covered()
        await Promise.allSettled([run.switched, sleep(HOLD_MIN_MS)])
        // Let the new page's first render land before collapsing (bounded — never a visible wait).
        await nextPaint()
        await new Promise<void>((r) => (window.requestIdleCallback ? window.requestIdleCallback(() => r(), { timeout: 250 }) : setTimeout(r, 0)))
        if (cancelled) return
        const back = originOf()
        if (reduced) {
          await disc.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: 'forwards' }).finished
        } else {
          const timing = { duration: IN_MS, easing: EASE_IN, fill: 'forwards' as const }
          text.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' })
          avatar.animate([{ transform: avatarAt(cx, cy, 1) }, { transform: avatarAt(back.x, back.y, AVATAR_PX / CARD_PX) }], timing)
          await disc.animate([{ transform: discAt(cx, cy, cover) }, { transform: discAt(back.x, back.y, r0) }], timing).finished
          // Landed exactly on the real avatar — hand over to it.
          if (!cancelled) {
            const fade = [{ opacity: 1 }, { opacity: 0 }]
            avatar.animate(fade, { duration: 120, fill: 'forwards' })
            await disc.animate(fade, { duration: 120, fill: 'forwards' }).finished
          }
        }
      } finally {
        if (!cancelled) { setRun(null); run.done() }
      }
    })()
    // Torn down mid-run (unmount): never strand the switch — start it and release the caller.
    return () => { cancelled = true; run.covered(); run.done() }
  }, [run, geo])

  if (!run || !geo) return null
  const { card } = run
  const { cx, cy, cover } = geo
  return createPortal(
    <div className={`fixed inset-0 z-[300] overflow-hidden ${themeClass} ws-clear`} aria-live="polite" role="status" data-testid="identity-switch-curtain">
      {/* The theme's own background with only a whisper of the identity's colour — calm enough to see on every
          switch; the avatar alone carries the vivid identity colour. */}
      <div
        ref={discRef}
        className="absolute rounded-full overflow-hidden themed-bg-primary"
        style={{ left: cx - cover, top: cy - cover, width: cover * 2, height: cover * 2, opacity: 0, willChange: 'transform' }}
      >
        <div className={`absolute inset-0 ${avatarBg(card.seed)} opacity-[0.14] [.theme-light_&]:opacity-[0.08]`} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(255,255,255,0.06),transparent_40%)]" />
        {/* Light theme: a LIGHT vignette — the edges fade toward the light home page's own stage tint (its cool
            indigo-grey, a shade lighter: #eef0f8), so the identity tint stays a soft glow around the avatar instead
            of a coloured wash fighting the pale background (dark / dim need none). */}
        <div className="absolute inset-0 hidden [.theme-light_&]:block bg-[radial-gradient(circle_at_center,transparent_18%,rgba(238,240,248,0.65)_48%,rgba(238,240,248,0.92)_70%)]" />
      </div>
      <div className="absolute" style={{ left: cx - CARD_PX / 2, top: cy - CARD_PX / 2, width: CARD_PX, height: CARD_PX }}>
        <div ref={ringRef} className="absolute inset-0 rounded-full border-2 themed-border" style={{ opacity: 0 }} />
        <div
          ref={avatarRef}
          className={`absolute inset-0 rounded-full flex items-center justify-center text-white font-semibold ${avatarBg(card.seed)} shadow-[0_14px_40px_rgba(0,0,0,0.25)]`}
          style={{ fontSize: 40, opacity: 0, willChange: 'transform' }}
        >
          {getInitials(card.title)}
        </div>
      </div>
      <div ref={textRef} className="absolute inset-x-0 text-center px-6" style={{ top: cy + CARD_PX / 2 + 16, opacity: 0 }}>
        <p className="themed-text text-2xl font-semibold tracking-tight truncate">{card.title}</p>
        {card.subtitle && <p className="mt-1 themed-text-muted text-sm truncate">{card.subtitle}</p>}
        {card.role && (
          // Tinted from the identity's own avatar gradient (low strength) — a neutral grey pill related to nothing
          // on screen and read as off in every colour; this one belongs to the avatar's colour family.
          <span className="relative mt-2.5 inline-flex items-center overflow-hidden rounded-full px-2.5 py-0.5 text-xs font-medium themed-text">
            <span aria-hidden className={`absolute inset-0 ${avatarBg(card.seed)} opacity-20 [.theme-light_&]:opacity-[0.16]`} />
            <span className="relative">{t(`roles.${card.role}`, { defaultValue: card.role })}</span>
          </span>
        )}
      </div>
    </div>,
    document.body,
  )
}
